'use client'

import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { Icon, IconComponent, SearchBar, Tree, TreeItem } from '@graftcode/design-system'

import { Sidebar } from '@common/core/components/Sidebar'

import { useLibraries } from '@/core/hooks'
import { useUser } from '@/core/hooks/useUser'
import { useVisionLibraryTree } from '@/core/hooks/useVisionLibraryTree'
import {
  selectExpandedItemsString,
  selectPathString,
  useExpandedItems,
  useNavigationStore,
} from '@/core/stores/navigationStore'
import { filterTreeItems, getExpandedIdsForSearch } from '@/core/utils/tree'

import styles from './Sidebar.module.scss'

/** Vision sidebar: library tree, search, and navigation. */
export function VisionSidebar() {
  const { user } = useUser()
  const { isLoading, isError } = useLibraries()
  const baseTreeItemsFromHook = useVisionLibraryTree()

  const pathString = useNavigationStore(selectPathString)
  const expandedItemsString = useNavigationStore(selectExpandedItemsString)
  const expandedItems = useExpandedItems()

  const [searchQuery, setSearchQuery] = useState('')
  const [keyboardShortcut, setKeyboardShortcut] = useState<string | undefined>(undefined)

  useEffect(() => {
    const userAgentData = (navigator as { userAgentData?: { mobile?: boolean; platform?: string } }).userAgentData
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      userAgentData?.mobile === true ||
      window.innerWidth < 768 // Common mobile breakpoint

    if (isMobile) {
      setKeyboardShortcut(undefined)
      return
    }

    const isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || userAgentData?.platform === 'macOS'
    setKeyboardShortcut(isMac ? '⌘N' : 'Ctrl+F')
  }, [])

  const baseTreeItems = baseTreeItemsFromHook

  // No modules hosted: hide the (empty) library panel entirely, leaving only the app nav.
  const hasNoModules = !isLoading && !isError && baseTreeItems.length === 0

  const treeItems = useMemo((): TreeItem[] => {
    if (searchQuery.trim()) {
      return filterTreeItems(baseTreeItems, searchQuery)
    }
    return baseTreeItems
  }, [baseTreeItems, searchQuery])

  useEffect(() => {
    if (baseTreeItems.length > 0 && !searchQuery.trim()) {
      const store = useNavigationStore.getState()
      if (!store.expandedItems.includes('root')) {
        store.setExpandedItems([...store.expandedItems, 'root'])
      }
    }
  }, [baseTreeItems, searchQuery])

  useEffect(() => {
    if (searchQuery.trim() && baseTreeItems.length > 0) {
      const searchExpandedIds = getExpandedIdsForSearch(baseTreeItems, searchQuery)
      const store = useNavigationStore.getState()
      const currentExpanded = store.expandedItems
      const needsUpdate =
        searchExpandedIds.length !== currentExpanded.length ||
        !searchExpandedIds.every((id) => currentExpanded.includes(id))

      if (needsUpdate) {
        store.setExpandedItems(searchExpandedIds)
      }
    }
  }, [searchQuery, baseTreeItems])

  const handleItemClick = (item: TreeItem) => {
    const newPath = item.id === 'root' ? [] : item.id.split('/')
    useNavigationStore.getState().navigateTo(newPath)
  }

  const handleItemToggle = (item: TreeItem, expanded: boolean) => {
    if (item.id === 'root' && !expanded) return

    const store = useNavigationStore.getState()
    if (expanded) {
      const newPath = item.id === 'root' ? [] : item.id.split('/')
      store.navigateTo(newPath)
    } else {
      store.toggleExpanded(item.id)
      const parentPath = item.id.split('/').slice(0, -1)
      store.navigateTo(parentPath)
    }
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)

  const handleSearchClear = () => setSearchQuery('')

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMacShortcut = event.metaKey && !event.ctrlKey && event.key === 'n'
      const isWindowsShortcut = event.ctrlKey && !event.metaKey && event.key === 'f'

      if (isMacShortcut || isWindowsShortcut) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        requestAnimationFrame(() => {
          const sidebar = document.querySelector('aside.sidebar')
          if (!sidebar) return

          const selectors = [
            `input[type="search"]`,
            `input[type="text"]`,
            `input[placeholder*="Search" i]`,
            `.${styles.content} input`,
            'aside.sidebar input',
          ]

          let searchInput: HTMLInputElement | null = null
          for (const selector of selectors) {
            searchInput = sidebar.querySelector(selector) as HTMLInputElement
            if (searchInput?.offsetParent != null) break
            searchInput = document.querySelector(selector) as HTMLInputElement
            if (searchInput?.offsetParent != null) break
            searchInput = null
          }

          searchInput?.focus()
          searchInput?.select()
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [])

  const treeKey = `${pathString}::${expandedItemsString}::${searchQuery}`

  const renderContent = () => {
    if (isError && !isLoading) {
      return null
    }

    return (
      <div className={styles.content}>
        <SearchBar
          className={styles.search}
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          size="md"
          shortcut={keyboardShortcut}
          version="gray"
        />
        {treeItems.length > 0 ? (
          <Tree
            key={treeKey}
            className={styles.tree}
            items={treeItems}
            expandedItemIds={expandedItems}
            activeItemId={pathString || undefined}
            showBadges
            onItemClick={handleItemClick}
            onItemToggle={handleItemToggle}
          />
        ) : searchQuery.trim() ? (
          <div className={styles.noResults}>
            <IconComponent icon={Icon.SearchRegular} className={styles.noResultsIcon} />
            <span className={styles.noResultsTitle}>No results found</span>
            <span className={styles.noResultsMessage}>Try a different search term</span>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <Sidebar
      selectedNavId="vision"
      isCollapsable={false}
      showWorkspaceSelector={!!user}
      navbarHidden={isError || hasNoModules}
    >
      {renderContent()}
    </Sidebar>
  )
}
